import { Button, Flex, Group, Stack, Text, TextInput, Title } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useLocalStorage } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { CurrencyInput, FormField } from "@/components/elements";
import { defaultCurrency } from "@/lib/currency";
import { Currency } from "@/types/currency";
import { formatDate } from "@/utils/formatDate";

import type { ElementType, FC } from "react";

interface Props {
  WrapperForFields: ElementType;
  wrapperProps: Record<string, unknown>;
  form: any;
  activeStep: number;
  onCancel?: () => void;
  loading: boolean;
  nextStep: () => void;
  isStepValid: (step: number) => boolean;
}

const EditStep1: FC<Props> = ({
  WrapperForFields,
  wrapperProps,
  form,
  activeStep,
  onCancel,
  loading,
  nextStep,
  isStepValid,
}) => {
  const { t } = useTranslation();

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  return (
    <Stack gap="md">
      <Title order={4} mb="sm">
        {t("components.CreateCarForm.titles.basic")}
      </Title>

      <WrapperForFields {...wrapperProps}>
        <TextInput
          label={t("car.carNickname")}
          placeholder={t("components.CreateCarForm.placeholders.nickname")}
          {...form.getInputProps("nickname")}
        />

        <FormField required>
          <TextInput
            label={t("car.owner")}
            placeholder={t("components.CreateCarForm.placeholders.owner")}
            {...form.getInputProps("owner")}
          />
        </FormField>
      </WrapperForFields>

      <WrapperForFields {...wrapperProps}>
        <FormField required>
          <CurrencyInput
            label={t("car.purchase_price")}
            value={(Number(form.values.purchase_price) || 0) * (Number(currency.value) || 1)}
            placeholder={t("components.CreateCarForm.placeholders.purchase_price", "Price")}
            currencySymbol={currency.symbol}
            onChange={(value) => {
              const adjustedValue = (Number(value) || 0) / (Number(currency.value) || 1);

              form.setFieldValue("purchase_price", adjustedValue);
            }}
          />
        </FormField>

        <FormField required>
          <DatePickerInput
            label={t("car.purchase_date")}
            placeholder={t("components.CreateCarForm.placeholders.purchase_date", "Select date")}
            valueFormat="YYYY-MM-DD"
            valueFormatter={(value) => formatDate(value.date as string)}
            {...form.getInputProps("purchase_date")}
          />
        </FormField>
      </WrapperForFields>

      <Group justify="space-between" mt="2em">
        <Text c="var(--mantine-color-primaryDark-5)" fw={500} fz="sm">
          Step {activeStep + 1} of 3
        </Text>

        <Flex justify="end" gap="md">
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={loading}
              aria-label={t("forms.cancelButton")}
              c="dark"
            >
              {t("forms.cancelButton")}
            </Button>
          )}

          <Button type="button" onClick={nextStep} disabled={!isStepValid(0)}>
            {t("forms.next")}
          </Button>
        </Flex>
      </Group>
    </Stack>
  );
};

export default EditStep1;
